import { useState, useEffect } from "react";
import notificationService from "../services/notificationService";

import configService from "../services/configService";

// Utility function to convert Base64 URL to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/\-/g, "+")
    .replace(/_/g, "/");

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function usePushNotifications() {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Check if push messaging is supported
    if ("serviceWorker" in navigator && "PushManager" in window) {
      setIsSupported(true);
      checkSubscription();
    } else {
      setIsSupported(false);
      setLoading(false);
    }
  }, []);

  const checkSubscription = async () => {
    try {
      const registration = await navigator.serviceWorker.register("/sw.js");
      const currentSubscription = await registration.pushManager.getSubscription();

      if (currentSubscription) {
        setIsSubscribed(true);
        setSubscription(currentSubscription);
      } else {
        setIsSubscribed(false);
      }
    } catch (err) {
      console.error("Error checking push subscription:", err);
      setError("Failed to check push subscription status.");
    } finally {
      setLoading(false);
    }
  };

  const subscribe = async () => {
    setLoading(true);
    setError(null);
    try {
      // Ask for permission explicitly if not granted
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        throw new Error("Notification permission denied");
      }

      const registration = await navigator.serviceWorker.ready;
      
      // Fetch public key from database via configService
      const configs = await configService.getSystemConfigs();
      const configList = configs.results || configs;
      const pubKeyConfig = configList.find(c => c.key === "vapid_public_key");
      
      if (!pubKeyConfig || !pubKeyConfig.value) {
        throw new Error("VAPID public key not found in system configurations.");
      }

      const newSubscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(pubKeyConfig.value),
      });

      // Format payload for backend
      const subscriptionData = JSON.parse(JSON.stringify(newSubscription));
      
      // Send to backend
      await notificationService.pushSubscribe(subscriptionData);

      setIsSubscribed(true);
      setSubscription(newSubscription);
      return true;
    } catch (err) {
      console.error("Error subscribing to push notifications:", err);
      setError(err.message || "Failed to subscribe to push notifications");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const unsubscribe = async () => {
    setLoading(true);
    setError(null);
    try {
      if (!subscription) return true;

      // Unsubscribe from browser PushManager
      const successful = await subscription.unsubscribe();
      
      if (successful) {
        // Send unsubscribe to backend
        await notificationService.pushUnsubscribe(subscription.endpoint);
        setIsSubscribed(false);
        setSubscription(null);
        return true;
      }
      return false;
    } catch (err) {
      console.error("Error unsubscribing:", err);
      setError("Failed to unsubscribe");
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    isSupported,
    isSubscribed,
    loading,
    error,
    subscribe,
    unsubscribe,
  };
}
